// ============================================================================
// Supplier field display — say "not set", never a bare dash
// ============================================================================
// Traced from the document-detail brief §5d: Category rendered as an em-dash on
// the review screen, the supplier record and the supplier pickers. The dash
// isn't a rendering bug — the value is genuinely absent (a large share of
// suppliers have never had a category set) — but "—" doesn't say that. It reads
// like a loading state, or like the field failed to fetch, and three screens
// each invented their own version of it ("—", "Uncategorised", "No category").
//
// So: one helper, one wording. An empty field says NOT SET, which is a fact
// about the record rather than a shrug. Matches the supplier-detail IA brief
// §4 ("empty values → 'Not set', not em-dash") and its distinction between a
// missing value and a real negative — this is the missing-value case.
// ============================================================================

export const NOT_SET = 'Not set';

const clean = (v) => (typeof v === 'string' ? v.trim() : v ? String(v) : '');

// A supplier's category, with its sub-category when there is one:
//   "Catering · Hot food"  ·  "Catering"  ·  "Not set"
export function categoryLabel(supplier, { fallback = NOT_SET } = {}) {
  const parts = [clean(supplier?.category), clean(supplier?.sub_category)].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : fallback;
}

// The category on its own — for places already showing sub-category separately.
export function categoryOnly(supplier, { fallback = NOT_SET } = {}) {
  return clean(supplier?.category) || fallback;
}

export function subCategoryOnly(supplier, { fallback = NOT_SET } = {}) {
  return clean(supplier?.sub_category) || fallback;
}

// True when there's nothing to show — lets a caller style the fallback as muted
// without re-deriving the emptiness test.
export const isUnset = (value) => !clean(value);

// Payment terms are stored as free text, and people type the number on its own.
// "Payment terms: 30" is meaningless without the unit — 30 what? — so a bare
// number gets one (supplier-detail IA §7a). Anything already written out
// ("30 days from invoice", "On completion") is left exactly as typed.
export function paymentTermsLabel(value, { fallback = NOT_SET } = {}) {
  const v = clean(value);
  if (!v) return fallback;
  return /^\d+$/.test(v) ? `${v} days` : v;
}
